import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  List,
  InputAdornment,
  Tooltip,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  DesignServices as ServicesIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import {
  HoneyServicesContainer,
  HoneyServicesHeader,
  HoneyServicesTitle,
  HoneyServicesSubtitle,
  HoneySearchContainer,
  HoneySearchInput,
  HoneyDropdown,
  HoneyDropdownItem,
  HoneyDropdownIcon,
  HoneyDropdownText,
  HoneyDropdownName,
  HoneyDropdownDesc,
  HoneyCountInfo,
  HoneyServicesList,
  HoneyServiceItem,
  HoneyServiceIcon,
  HoneyServiceContent,
  HoneyServiceName,
  HoneyServiceDescription,
  HoneyServiceActions,
  HoneyActionButton,
  HoneyEmptyState,
  HoneyEmptyIcon,
  HoneyExpandedContent,
  HoneyDescriptionInput,
  HoneyExpandedActions,
  getServiceIcon,
  honeyServiceColors
} from './styled';

const ServicesView = ({
  selectedServices,
  searchQuery,
  showDropdown,
  filteredServices,
  isSaving,
  isLoadingServices,
  hasChanges,
  error,
  editingService,
  editDialogOpen,
  disableSaveButton,
  handleSearchChange,
  handleServiceSelect,
  handleRemoveService,
  handleSaveServices,
  movePriorityUp,
  movePriorityDown,
  startEditing,
  saveServiceChanges,
  cancelEditing,
  handleDescriptionChange,
  handleClickAway
}) => {
  // Always sort by priority before rendering
  const sortedServices = [...selectedServices].sort((a, b) => a.priority - b.priority);

  return (
    <HoneyServicesContainer sx={{ pt: 3 }}>
      <HoneyServicesHeader>
        <HoneyServicesTitle>
          <ServicesIcon />
          Services
        </HoneyServicesTitle>
        <HoneyServicesSubtitle>
          Available amenities in this coworking space
        </HoneyServicesSubtitle>
      </HoneyServicesHeader>

      {/* Search Services */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <HoneySearchContainer>
          <HoneySearchInput
            placeholder="Search services to add (WiFi, Coffee, Parking, Meeting Rooms...)"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            disabled={isLoadingServices}
          />
          
          {showDropdown && filteredServices.length > 0 && (
            <HoneyDropdown>
              {filteredServices.map((service) => (
                <HoneyDropdownItem
                  key={service.slug}
                  onClick={() => handleServiceSelect(service)}
                >
                  <HoneyDropdownIcon>
                    {getServiceIcon(service.name)}
                  </HoneyDropdownIcon>
                  <HoneyDropdownText>
                    <HoneyDropdownName variant="body2">
                      {service.name}
                    </HoneyDropdownName>
                    <HoneyDropdownDesc variant="caption">
                      {service.description || 'No description available'}
                    </HoneyDropdownDesc>
                  </HoneyDropdownText>
                </HoneyDropdownItem>
              ))}
            </HoneyDropdown>
          )}
        </HoneySearchContainer>
      </ClickAwayListener>

      {selectedServices.length > 0 && (
        <HoneyCountInfo>
          {selectedServices.length} services available
        </HoneyCountInfo>
      )}

      {/* Services List */}
      {selectedServices.length > 0 ? (
        <HoneyServicesList>
          <List disablePadding>
            {sortedServices.map((service, index) => (
              <HoneyServiceItem key={service.slug} disablePadding>
                <HoneyServiceIcon>
                  {getServiceIcon(service.name)}
                </HoneyServiceIcon>
                
                <HoneyServiceContent>
                  <HoneyServiceName>
                    {service.name}
                  </HoneyServiceName>
                  <HoneyServiceDescription>
                    {service.description || 'No description available'}
                  </HoneyServiceDescription>
                </HoneyServiceContent>
                
                <HoneyServiceActions>
                  <Tooltip title="Move up">
                    <span>
                      <HoneyActionButton
                        size="small"
                        onClick={() => movePriorityUp(service.slug)}
                        disabled={isSaving || index === 0}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </HoneyActionButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title="Move down">
                    <span>
                      <HoneyActionButton
                        size="small"
                        onClick={() => movePriorityDown(service.slug)}
                        disabled={isSaving || index === sortedServices.length - 1}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </HoneyActionButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title="Edit">
                    <HoneyActionButton
                      size="small"
                      onClick={() => startEditing(service)}
                      disabled={isSaving}
                    >
                      <EditIcon fontSize="small" />
                    </HoneyActionButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete">
                    <HoneyActionButton
                      variant="delete"
                      size="small"
                      onClick={() => handleRemoveService(service.slug)}
                      disabled={isSaving}
                    >
                      <DeleteIcon fontSize="small" />
                    </HoneyActionButton>
                  </Tooltip>
                </HoneyServiceActions>
              </HoneyServiceItem>
            ))}
          </List>
        </HoneyServicesList>
      ) : (
        <HoneyEmptyState>
          <HoneyEmptyIcon>⚙️</HoneyEmptyIcon>
          <Typography variant="body1" sx={{ color: honeyServiceColors.lightBrown }}>
            No services available for this coworking space.
          </Typography>
          <Typography variant="body2" sx={{ color: honeyServiceColors.lightBrown, mt: 1 }}>
            Search above to add your first service
          </Typography>
        </HoneyEmptyState>
      )}

      {/* Save Button */}
      {!disableSaveButton && hasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveServices}
            disabled={isSaving}
            sx={{
              background: honeyServiceColors.honeyGradient,
              color: honeyServiceColors.honeyBrown,
              borderRadius: '50px',
              padding: '12px 32px',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: `0 8px 32px ${honeyServiceColors.goldenHoney}50`,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: honeyServiceColors.honeycombGradient,
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${honeyServiceColors.goldenHoney}70`,
              },
              '&:disabled': {
                opacity: 0.6,
                transform: 'none',
              },
            }}
          >
            {isSaving ? 'Saving...' : '💾 Save Changes'}
          </Button>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={cancelEditing}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          {editingService && (
            <HoneyDescriptionInput
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={editingService.description || ''}
              onChange={handleDescriptionChange}
              placeholder="Enter service description..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelEditing} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={saveServiceChanges} 
            variant="contained" 
            startIcon={<CheckIcon />}
            sx={{
              background: honeyServiceColors.honeyGradient,
              color: honeyServiceColors.honeyBrown,
              '&:hover': {
                background: honeyServiceColors.honeycombGradient,
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </HoneyServicesContainer>
  );
};

export default ServicesView;